import React, { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';

import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControlWithFocus,
  FormMessageWithValidation,
  useFormValidation,
} from '../../../../shared/components/form';
import { getGstRateByCountryCode } from '../../../../shared/constants/gstCountry';
import { fetchCreator, updateCreator } from '../../../../shared/actions/creatorActions';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { showSuccess } from '../../../../shared/utils/swalHandler';
import VatInfoModal from '../../../Modals/EarningsPayout/VatInfoModal';
import { createGstNumberSchema } from '../../../../shared/validations/transaction';

const VAT_INFO_CARDS = [
  {
    icon: 'fa-shield-halved',
    title: 'Compliance',
    text: 'Ensure you are compliant with tax regulations.',
  },
  {
    icon: 'fa-receipt',
    title: 'GST Collection',
    text:
      'Enter your GST number for Juicy House to collect the appropriate tax at checkout. For any sales made while your GST number was missing you are responsible for reviewing your transaction history, calculating the GST that should have been applied, and including it in your next GST return. You can export earnings reports for the selected year by tapping Export Data at the bottom of the Earnings & Payouts screen.',
  },
];

const getCountryDisplayValue = (code, list) => {
  const trimmed = code?.trim?.() ?? '';
  if (!trimmed) return '';
  const found = list.find((c) => c.code.toLowerCase() === trimmed.toLowerCase());
  return found ? found.name : trimmed;
};

const VatNumber = ({
  creator: creatorInfo,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const [valueAddedTaxModal, setValueAddedTaxModal] = useState(false);

  const form = useForm({
    resolver: zodResolver(createGstNumberSchema()),
    defaultValues: {
      country: creatorInfo?.user_data?.country || '',
      gst_number: creatorInfo?.user_data?.gst_number || '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (creatorInfo?.user_data) {
      form.reset({
        country: creatorInfo.user_data.country || '',
        gst_number: creatorInfo.user_data.gst_number || '',
      });
    }
  }, [creatorInfo?.user_data, form]);

  const { validateAndFocus } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  const onSubmit = useCallback(
    async (values) => {
      const isValid = await validateAndFocus();
      if (!isValid) return;

      try {
        const result = await updateCreator({
          gst_number: values.gst_number,
        });

        if (result.success) {
          showSuccess('Success', 'GST number updated successfully');

          if (creatorInfo?.user_data?.username) {
            await dispatch(fetchCreator(creatorInfo.user_data.username));
          }
        } else {
          toast.error(result.error || result.message || 'Failed to update GST number');
        }
      } catch (error) {
        toast.error(error.message || 'An unexpected error occurred');
      }
    },
    [validateAndFocus, dispatch, creatorInfo?.user_data?.username]
  );

  return (
    <div className="modern-earnings-payout-page">
      <ModernHeader
        customTitle="GST Number"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <div className='vat_top_sec'>
            <span><i className='fa-solid fa-receipt'></i></span>
            <p>
              To collect GST we need to verify that you are registered. Please enter your GST number below.
            </p>
          </div>

          <div className='cm_dark_form mb-4'>
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem className='form-group'>
                  <FormLabel className='text-white'>Country</FormLabel>
                  <FormControlWithFocus fieldName="country">
                    <input
                      type='text'
                      className='form-control cm_form_control'
                      placeholder='Enter country'
                      disabled={!!creatorInfo?.user_data?.country}
                      value={getCountryDisplayValue(field.value, getGstRateByCountryCode)}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  </FormControlWithFocus>
                  <FormMessageWithValidation
                    fieldName="country"
                    useValidation
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="gst_number"
              render={({ field }) => (
                <FormItem className='form-group'>
                  <FormLabel className='text-white'>GST Number</FormLabel>
                  <FormControlWithFocus fieldName="gst_number">
                    <div className='cm_with_icon'>
                      <input
                        type='text'
                        className='form-control cm_form_control'
                        placeholder='Enter GST number'
                        disabled={!!creatorInfo?.user_data?.gst_number}
                        {...field}
                      />
                      <span
                        className='modal_btn with_pink_hover'
                        onClick={() => setValueAddedTaxModal(true)}
                      >
                        <i className='fa-solid fa-circle-question'></i>
                      </span>
                    </div>
                  </FormControlWithFocus>
                  <FormMessageWithValidation
                    fieldName="gst_number"
                    useValidation
                  />
                </FormItem>
              )}
            />
          </div>

          <h2>Why Provide a GST Number?</h2>

          {VAT_INFO_CARDS.map(({ icon, title, text }) => (
            <div key={title} className='vat_card'>
              <div>
                <span><i className={`fa-solid ${icon}`} /></span>
              </div>
              <div className='w-full'>
                <h4>{title}</h4>
                <p>{text}</p>
              </div>
            </div>
          ))}

          <div className='need_help_card'>
            <span><i className='fa-solid fa-headset'></i></span>
            <h3>Need Help?</h3>
            <p>Our support team is here to assist you</p>
            <button
              type="button"
              className='btn btn_primary_outline'
              onClick={() => navigate.toContactSupport()}
            >Contact Support</button>
          </div>

          {!creatorInfo?.user_data?.gst_number && (
            <>
              <hr className='cm_hr' />
              <button
                type='submit'
                className='btn btn_primary rounded_8 w-full'
                disabled={form.formState.isSubmitting || !form.formState.isValid}
              >
                Submit GST Number
              </button>
            </>
          )}
        </form>
      </Form>

      <VatInfoModal
        isOpen={valueAddedTaxModal}
        toggle={() => setValueAddedTaxModal(false)}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    creator: state.creator?.creator,
  };
};

export default connect(mapStateToProps)(VatNumber);
